import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import Footer from '../components/Footer';
import SponsorBar from '../components/SponsorBar';
import useStore from '../store/store';

const useCountdown = (targetDateISO) => {
  const getTimeLeft = useCallback(() => {
    if (!targetDateISO) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    const diff = new Date(targetDateISO) - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
      done: false,
    };
  }, [targetDateISO]);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);
  useEffect(() => {
    setTimeLeft(getTimeLeft());
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, [getTimeLeft]);
  return timeLeft;
};

const parseSeasonNumber = (label = '') => {
  const match = String(label).match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const championsData = [
  {
    id: 'season-1',
    season: 'SEASON 1',
    year: '2024',
    team: 'HASHIRAS',
    image: '/images/season_1.webp',
    alt: 'Season 1 winners Hashiras',
  },
  {
    id: 'season-2',
    season: 'SEASON 2',
    year: '2024',
    team: 'HASHIRAS',
    image: '/images/season_2.webp',
    alt: 'Season 2 winners Hashiras',
  },
  {
    id: 'season-3',
    season: 'SEASON 3',
    year: '2025',
    team: 'FALAKNAMA FIGHTERS',
    image: '/images/season_3.webp',
    alt: 'Season 3 winners Falaknama Fighters',
  },
  {
    id: 'season-4',
    season: 'SEASON 4',
    year: '2025',
    team: 'KANYARASI',
    image: '/images/season_4.webp',
    alt: 'Season 4 winners Kanyarasi',
  },
];

const seasonHistoryByNumber = {
  1: { winner: 'HASHIRAS', teams: '8', prizePool: '₹ 25,000', date: 'November 2024', championLogo: '/images/season_1.webp' },
  2: { winner: 'HASHIRAS', teams: '8', prizePool: '₹ 40,000', date: 'December 2024', championLogo: '/images/season_2.webp' },
  3: {
    winner: 'FALAKNAMA FIGHTERS',
    teams: '12',
    prizePool: '₹ 60,000',
    date: 'July 2025',
    championLogo: '/images/season_3.webp',
  },
  4: { winner: 'KANYARASI', teams: '16', prizePool: '₹ 75,000', date: 'October 19, 2025', championLogo: '/images/season_4.webp' },
  5: { winner: 'TBD', teams: '16', prizePool: '₹ TBD', date: 'Coming Soon', championLogo: null },
};

const introShowcaseSlides = [
  {
    id: 'player-auction',
    label: 'PLAYER AUCTION',
    image: '/images/auction.webp',
    alt: 'VPL player auction showcase',
  },
  {
    id: 'team-ownership',
    label: 'TEAM OWNERSHIP',
    image: '/images/teamownership.webp',
    alt: 'VPL team ownership and franchise identity',
  },
  {
    id: 'league-stage',
    label: 'LEAGUE STAGE',
    image: '/images/league.webp',
    alt: 'VPL league stage competition',
  },
  {
    id: 'lan-finals',
    label: 'LAN FINALS',
    image: '/images/lan event.webp',
    alt: 'VPL LAN finals highlight',
  },
  {
    id: 'community',
    label: 'COMMUNITY',
    image: '/images/community.webp',
    alt: 'VPL community and competition highlight',
  },
  {
    id: 'champions',
    label: 'CHAMPIONS',
    image: '/images/season_4.png',
    alt: 'VPL champion team photo',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const storeSeason = useStore((s) => s.currentSeason);
  // Prize pool reveal flags — driven by Supabase seasons columns:
  //   show_prize_pool (boolean)  — flip to true to reveal
  //   prize_pool_display (text)  — e.g. "₹1,00,000"
  const showPrizePool = storeSeason?.show_prize_pool === true;
  const prizePoolDisplay = storeSeason?.prize_pool_display || '₹1,00,000';
  const aboutSectionRef = useRef(null);
  const partnersSectionRef = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const footerSectionRef = useRef(null);
  const wrapperRef = useRef(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedTimelineSeasonId, setSelectedTimelineSeasonId] = useState('');
  const [isTimelineDetailsFading, setIsTimelineDetailsFading] = useState(false);
  const [activeIntroSlide, setActiveIntroSlide] = useState(0);
  const [shouldAnimateIntroStats, setShouldAnimateIntroStats] = useState(false);
  const [hasAnimatedIntroStats, setHasAnimatedIntroStats] = useState(false);
  const [introStats, setIntroStats] = useState({
    seasons: 0,
    players: 0,
    teams: 0,
    prizePool: 0,
  });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 }); // eslint-disable-line no-unused-vars

  // Compute countdownTarget early (before bannerState useMemo) so the hook
  // call order is stable and bannerState TDZ is avoided.
  const countdownTarget = useMemo(() => {
    if (!seasons || seasons.length === 0) return null;
    const getPriorityDate = (s) => s.registration_start_date || s.start_date || s.end_date || null;
    const latest = [...seasons].sort((a, b) => new Date(getPriorityDate(b) || 0) - new Date(getPriorityDate(a) || 0))[0];
    const now = Date.now();
    const start = latest.registration_start_date ? new Date(latest.registration_start_date).getTime() : null;
    const end   = latest.registration_end_date   ? new Date(latest.registration_end_date).getTime()   : null;
    if (start && now < start) return latest.registration_start_date; // upcoming → count to open
    if (start && end && now >= start && now < end) return latest.registration_end_date; // live → count to close
    return null;
  }, [seasons]);

  const countdownLabel = useMemo(() => {
    if (!seasons || seasons.length === 0) return 'REGISTRATION OPENS IN';
    const getPriorityDate = (s) => s.registration_start_date || s.start_date || s.end_date || null;
    const latest = [...seasons].sort((a, b) => new Date(getPriorityDate(b) || 0) - new Date(getPriorityDate(a) || 0))[0];
    const start = latest.registration_start_date ? new Date(latest.registration_start_date).getTime() : null;
    return start && Date.now() < start ? 'REGISTRATION OPENS IN' : 'REGISTRATION CLOSES IN';
  }, [seasons]);

  // Roadmap milestones — pulled from Supabase (seasons.roadmap_milestones) when
  // available, falling back to this hardcoded array so the section never breaks
  // if the column is empty or seasons haven't loaded yet.
  const fallbackRoadmapMilestones = [
    { date: '20–29 Jun', name: 'Registrations', sub: 'Open now',           status: 'active'   },
    { date: '04–05 Jul', name: 'Player Auction', sub: 'Live bidding',       status: 'upcoming' },
    { date: '11–28 Jul', name: 'League Stage',   sub: 'Group matches',      status: 'upcoming' },
    { date: '01-07 Aug',    name: 'LAN Prep',       sub: 'Shoots · Jerseys',   status: 'upcoming' },
    { date: '08–09 Aug', name: 'LAN Finals',     sub: 'Our Second Lan Event', status: 'lan'      },
  ];

  const roadmapMilestones = useMemo(() => {
    if (!seasons || seasons.length === 0) return fallbackRoadmapMilestones;
    const getPriorityDate = (s) => s.registration_start_date || s.start_date || s.end_date || null;
    const latest = [...seasons].sort((a, b) => new Date(getPriorityDate(b) || 0) - new Date(getPriorityDate(a) || 0))[0];
    if (latest && Array.isArray(latest.roadmap_milestones) && latest.roadmap_milestones.length > 0) {
      return latest.roadmap_milestones;
    }
    return fallbackRoadmapMilestones;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasons]);

  // Countdown driven by Supabase season dates — update registration_start_date /
  // registration_end_date on the Season 5 row and it reflects here with no redeploy.
  const heroCountdown = useCountdown(countdownTarget);

  // Load seasons data from API and store
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        const { default: useStore } = await import('../store/store');
        const store = useStore.getState();

        // First, try to get existing data from store
        if (store && store.seasons && store.seasons.length > 0) {
          setSeasons(store.seasons);
        } else {
          await store.fetchSeasons();
          const updatedStore = useStore.getState();
          if (updatedStore.seasons) {
            setSeasons(updatedStore.seasons);
          }
        }

        // Subscribe to store changes for real-time updates
        const unsubscribe = useStore.subscribe((state) => {
          if (state.seasons) {
            setSeasons(state.seasons);
          }
        });

        return unsubscribe;
      } catch (error) {
        console.log('Store not available or API call failed, using fallback data:', error);
      }
    };

    const cleanup = loadSeasons();
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then((unsubscribe) => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
    };
  }, []);

  const bannerState = useMemo(() => {
    if (!seasons || seasons.length === 0) {
      return {
        seasonName: 'NEW SEASON',
        headline: 'NEW SEASON COMING SOON',
        registrationOpen: false,
      };
    }

    const getPriorityDate = (season) => {
      return season.registration_start_date || season.start_date || season.end_date || null;
    };

    const sortedSeasons = [...seasons].sort((a, b) => {
      const aDate = new Date(getPriorityDate(a) || 0);
      const bDate = new Date(getPriorityDate(b) || 0);
      return bDate - aDate;
    });
    const latestSeason = sortedSeasons[0];

    const parseDate = (value) => (value ? new Date(value) : null);
    const now = new Date();
    const registrationStart = parseDate(latestSeason.registration_start_date);
    const registrationEnd = parseDate(latestSeason.registration_end_date);
    const seasonStart = parseDate(latestSeason.start_date);
    const seasonEnd = parseDate(latestSeason.end_date);

    const registrationOpen = registrationStart
      ? registrationEnd
        ? now >= registrationStart && now <= registrationEnd
        : now >= registrationStart
      : false;
    const registrationUpcoming = registrationStart ? now < registrationStart : false;
    const seasonLive = seasonStart ? (seasonEnd ? now >= seasonStart && now <= seasonEnd : now >= seasonStart) : false;

    const seasonName = latestSeason.name || 'NEW SEASON';
    let headline = `${seasonName} IS LIVE!`;

    if (registrationOpen) {
      headline = `${seasonName} REGISTRATIONS OPEN`;
    } else if (registrationUpcoming) {
      headline = `${seasonName} REGISTRATIONS OPENING SOON`;
    } else if (registrationStart && registrationEnd && now > registrationEnd) {
      // Registration has ended → show the next phase from the roadmap.
      // Reads the milestone marked 'active' in Supabase (roadmap_milestones);
      // falls back to "IS LIVE" so it never reverts to "COMING SOON".
      const milestones = Array.isArray(latestSeason.roadmap_milestones)
        ? latestSeason.roadmap_milestones
        : [];
      const activeMs = milestones.find((m) => m.status === 'active');
      const nextMs = activeMs || milestones.find((m) => m.status === 'upcoming');
      headline = nextMs && nextMs.name
        ? `${seasonName} · ${String(nextMs.name).toUpperCase()}`
        : `${seasonName} IS LIVE!`;
    } else if (!seasonLive) {
      headline = `${seasonName} IS LIVE!`;
    }

    return {
      seasonName,
      headline,
      registrationOpen,
      registrationUpcoming,
    };
  }, [seasons]);

  const timelineEntries = useMemo(() => {
    if (seasons.length > 0) {
      return [...seasons]
        .map((season) => {
          const seasonNumber = parseSeasonNumber(season.name);
          const fallback = seasonHistoryByNumber[seasonNumber] || {};
          const formattedDate =
            seasonNumber === 5
              ? 'Coming Soon'
              : season.start_date
                ? new Date(season.start_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : fallback.date || 'Coming soon...';

          return {
            id: season.id,
            seasonName: season.name || `Season ${seasonNumber || ''}`.trim(),
            winner: fallback.winner || 'TBD',
            teams: fallback.teams || 'TBD',
            prizePool: fallback.prizePool || 'TBD',
            date: formattedDate,
            championLogo: fallback.championLogo || null,
          };
        })
        .sort((a, b) => parseSeasonNumber(a.seasonName) - parseSeasonNumber(b.seasonName));
    }

    return Object.keys(seasonHistoryByNumber)
      .map(Number)
      .sort((a, b) => a - b)
      .map((seasonNumber) => ({
        id: `season-${seasonNumber}`,
        seasonName: `Season ${seasonNumber}`,
        winner: seasonHistoryByNumber[seasonNumber].winner,
        teams: seasonHistoryByNumber[seasonNumber].teams,
        prizePool: seasonHistoryByNumber[seasonNumber].prizePool,
        date: seasonHistoryByNumber[seasonNumber].date,
        championLogo: seasonHistoryByNumber[seasonNumber].championLogo,
      }));
  }, [seasons]);

  useEffect(() => {
    if (timelineEntries.length === 0) {
      return;
    }

    if (!timelineEntries.some((entry) => entry.id === selectedTimelineSeasonId)) {
      setSelectedTimelineSeasonId(timelineEntries[timelineEntries.length - 1].id);
    }
  }, [timelineEntries, selectedTimelineSeasonId]);

  useEffect(() => {
    if (!selectedTimelineSeasonId) {
      return;
    }

    setIsTimelineDetailsFading(true);
    const timer = setTimeout(() => setIsTimelineDetailsFading(false), 200);
    return () => clearTimeout(timer);
  }, [selectedTimelineSeasonId]);

  useEffect(() => {
    const slideTimer = setInterval(() => {
      setActiveIntroSlide((prev) => (prev + 1) % introShowcaseSlides.length);
    }, 5000);

    return () => clearInterval(slideTimer);
  }, []);

  useEffect(() => {
    if (!wrapperRef.current || !aboutSectionRef.current || hasAnimatedIntroStats) {
      return undefined;
    }

    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldAnimateIntroStats(true);
          }
        });
      },
      {
        root: wrapperRef.current,
        threshold: 0.35,
      }
    );

    statsObserver.observe(aboutSectionRef.current);

    return () => {
      statsObserver.disconnect();
    };
  }, [hasAnimatedIntroStats]);

  useEffect(() => {
    if (!shouldAnimateIntroStats || hasAnimatedIntroStats) {
      return undefined;
    }

    const targets = {
      seasons: 5,
      players: 300,
      teams: 64,
      prizePool: 200000,
    };

    const duration = 1200;
    let animationFrameId;
    let startTime;

    const animateCounters = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      setIntroStats({
        seasons: Math.round(targets.seasons * progress),
        players: Math.round(targets.players * progress),
        teams: Math.round(targets.teams * progress),
        prizePool: Math.round(targets.prizePool * progress),
      });

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateCounters);
      } else {
        setHasAnimatedIntroStats(true);
      }
    };

    animationFrameId = requestAnimationFrame(animateCounters);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [shouldAnimateIntroStats, hasAnimatedIntroStats]);

const selectedTimelineEntry =
  timelineEntries.find((entry) => entry.id === selectedTimelineSeasonId) ||
  timelineEntries[0] ||
  null;

  useEffect(() => {
    const handleScroll = () => {
      if (!section2Ref.current || !section3Ref.current || !wrapperRef.current) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const section2Rect = section2Ref.current.getBoundingClientRect();
      const section3Rect = section3Ref.current.getBoundingClientRect();
      // Calculate relative to the wrapper, not the viewport
      const section2Top = section2Rect.top - wrapperRect.top;
      const section2Bottom = section2Rect.bottom - wrapperRect.top;
      const section3Top = section3Rect.top - wrapperRect.top;
      const section3Bottom = section3Rect.bottom - wrapperRect.top;
      const wrapperHeight = wrapperRect.height;

      // Check if section2 is centered in the wrapper
      const section2InView = section2Top < wrapperHeight / 2 && section2Bottom > wrapperHeight / 2;
      // Check if section3 is centered in the wrapper
      const section3InView = section3Top < wrapperHeight / 2 && section3Bottom > wrapperHeight / 2;

      // Update classes based on which section is in view
      if (section3InView) {
        document.body.classList.add('scrolled-3');
        document.body.classList.remove('scrolled');
      } else if (section2InView) {
        document.body.classList.add('scrolled');
        document.body.classList.remove('scrolled-3');
      } else {
        document.body.classList.remove('scrolled');
        document.body.classList.remove('scrolled-3');
      }
    };
    const wrapper = wrapperRef.current;
    wrapper.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    return () => wrapper.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) {
      return undefined;
    }

    const revealTargets = [
      aboutSectionRef.current,
      partnersSectionRef.current,
      section2Ref.current,
      section3Ref.current,
      footerSectionRef.current,
    ].filter(Boolean);

    if (revealTargets.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('home-reveal--visible');
          }
        });
      },
      {
        root: wrapperRef.current,
        threshold: 0.2,
      }
    );

    revealTargets.forEach((target) => observer.observe(target));

    return () => {
      revealTargets.forEach((target) => observer.unobserve(target));
      observer.disconnect();
    };
  }, []);

  return (
    <div className="home-page-wrapper" ref={wrapperRef}>
      <div className="home-container">
        <div
          className="season-announcement"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.6rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div className="hero-season-badge">SEASON 5</div>
          <h1 className="home-title">VALORANT PREMIER LEGENDS</h1>
          <div
            className="season-4-banner"
            style={{
              background: 'linear-gradient(135deg, rgba(60,226,194,0.1), rgba(0,0,0,0.7))',
              padding: '2.55rem',
              borderRadius: '15px',
              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              textAlign: 'center',
              maxWidth: '95vw',
              width: 'max-content',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #3CE2C2, transparent)',
                animation: 'scanline 2s linear infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #3CE2C2, transparent)',
                animation: 'scanline 2s linear infinite reverse',
              }}
            />
            <h2
              style={{
                fontSize: '3em',
                margin: '0',
                background: 'linear-gradient(45deg, #3CE2C2, #2AA090)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'textGlow 2s ease-in-out infinite',
              }}
            >
              {bannerState.headline}
            </h2>
            {/* ── HERO COUNTDOWN ─────────────────────────────── */}
            {countdownTarget && !heroCountdown.done && (
              <div className="hero-countdown">
                <span className="hero-countdown__label">{countdownLabel}</span>
                <div className="hero-countdown__tiles">
                  {[
                    { v: heroCountdown.days,    u: 'DAYS'    },
                    { v: heroCountdown.hours,   u: 'HOURS'   },
                    { v: heroCountdown.minutes, u: 'MINUTES' },
                    { v: heroCountdown.seconds, u: 'SECONDS' },
                  ].map(({ v, u }) => (
                    <div key={u} className="hero-countdown__tile">
                      <span className="hero-countdown__num">
                        {String(v).padStart(2, '0')}
                      </span>
                      <span className="hero-countdown__unit">{u}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* ──────────────────────────────────────────────── */}

            <p
              style={{
                fontSize: '1.5em',
                color: '#fff',
                marginTop: '1rem',
                textShadow: '0 0 10px rgba(60,226,194,0.5)',
              }}
            >
              The Ultimate Vpl Experience
            </p>

            <div
              className="hero-cta-row"
              style={{
                marginTop: '1.6rem',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
              }}
            >
              <div
                className="hero-cta-group"
                style={{
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {bannerState.registrationOpen ? (
                  <button
                    className="register-button hero-action-btn"
                    onClick={() => (window.location.href = '/register')}
                    style={{
                      padding: '1rem 2rem',
                      fontSize: '1.2em',
                      background: '#3CE2C2',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'black',
                      cursor: 'pointer',
                      animation: 'pulse 1.5s infinite',
                      transition: 'transform 0.2s',
                      fontWeight: 'bold',
                    }}
                  >
                    REGISTER NOW
                  </button>
                ) : (
                  <>
                    <button
                      className="hero-action-btn"
                      onClick={() => navigate('/fixtures')}
                      style={{
                        padding: '1rem 2rem',
                        fontSize: '1.2em',
                        background: '#3CE2C2',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'black',
                        cursor: 'pointer',
                        animation: 'pulse 1.5s infinite',
                        transition: 'transform 0.2s',
                        fontWeight: 'bold',
                      }}
                    >
                      VIEW MATCHES
                    </button>
                    <button
                      className="hero-action-btn"
                      onClick={() => navigate('/teams')}
                      style={{
                        padding: '1rem 2rem',
                        fontSize: '1.2em',
                        background: '#3CE2C2',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'black',
                        cursor: 'pointer',
                        animation: 'pulse 1.5s infinite',
                        transition: 'transform 0.2s',
                        fontWeight: 'bold',
                      }}
                    >
                      VIEW TEAMS
                    </button>
                    {showPrizePool && (
                      <button
                        className="hero-action-btn"
                        onClick={() => navigate('/rules?tab=prizepool')}
                        style={{
                          padding: '1rem 2rem',
                          fontSize: '1.2em',
                          background: '#3CE2C2',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'black',
                          cursor: 'pointer',
                          animation: 'pulse 1.5s infinite',
                          transition: 'transform 0.2s',
                          fontWeight: 'bold',
                        }}
                      >
                        VIEW PRIZE POOL
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div
              className="hero-stats-row"
              style={{
                marginTop: '1.5rem',
                display: 'flex',
                gap: '1.6rem',
                justifyContent: 'center',
              }}
            >
              <div
                className="stat-box"
                style={{
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-in',
                }}
              >
                <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#3CE2C2' }}>16</div>
                <div style={{ color: '#fff' }}>Teams</div>
              </div>
              {showPrizePool && (
              <div
                className="stat-box stat-box--featured"
                style={{
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-in 0.2s',
                }}
              >
                <div style={{ fontSize: '2.2em', fontWeight: 'bold', color: '#3CE2C2' }}>{prizePoolDisplay}</div>
                <div style={{ color: '#fff' }}>Prize Pool</div>
              </div>
              )}
              <div
                className="stat-box"
                style={{
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-in 0.4s',
                }}
              >
                <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#3CE2C2' }}>5</div>
                <div style={{ color: '#fff' }}>Weeks</div>
              </div>
            </div>
          </div>

          {/* ── SEASON 5 ROADMAP ───────────────────────────── */}
          <div className="hero-roadmap">
            <div className="hero-roadmap__eyebrow">Season 5 · 2026 Roadmap</div>
            <div className="hero-roadmap__track">
              {roadmapMilestones.map((m, i) => (
                <div key={i} className={`hero-roadmap__ms hero-roadmap__ms--${m.status}`}>
                  {m.status === 'active' && <div className="hero-roadmap__now">● NOW</div>}
                  <div className="hero-roadmap__dot" />
                  <div className="hero-roadmap__ms-date">{m.date}</div>
                  <div className="hero-roadmap__ms-name">{m.name}</div>
                  <div className="hero-roadmap__ms-sub">{m.sub}</div>
                </div>
              ))}
            </div>
          </div>
          {/* ──────────────────────────────────────────────── */}

        </div>
      </div>
      <div className="home-partners home-reveal" ref={partnersSectionRef}>
        <SponsorBar />
      </div>
      <div className="home-container-about home-reveal" ref={aboutSectionRef}>
        <div className="vpl-intro-shell">
          <div className="vpl-intro-content">
            <h2 className="vpl-intro-heading">WHAT IS VPL?</h2>
            <p className="vpl-intro-subheading">India&apos;s Community-Driven Valorant Premier Ecosystem</p>
            <p className="vpl-intro-copy">
              Valorant Premier Legends is a structured competitive Valorant ecosystem built for amateur and
              semi-professional players.
            </p>
            <p className="vpl-intro-copy">
              Designed around player auctions, team ownership, league stages, playoffs, and LAN finals, VPL gives
              rising players a chance to experience a professional esports environment.
            </p>

            <p className="vpl-intro-list-title">KEY FEATURES</p>

            <ul className="vpl-intro-list" aria-label="VPL format highlights">
              <li className="vpl-intro-list-item">Player Auctions</li>
              <li className="vpl-intro-list-item">Team Ownership</li>
              <li className="vpl-intro-list-item">League Stage Format</li>
              <li className="vpl-intro-list-item">Playoffs &amp; LAN Finals</li>
              <li className="vpl-intro-list-item">Community-Driven Competition</li>
            </ul>

            <div className="vpl-intro-stats" aria-label="VPL introduction statistics">
              <div className="vpl-intro-stat-card">
                <span className="vpl-intro-stat-label">SEASONS</span>
                <strong className="vpl-intro-stat-value">{introStats.seasons}</strong>
              </div>
              <div className="vpl-intro-stat-card">
                <span className="vpl-intro-stat-label">PLAYERS</span>
                <strong className="vpl-intro-stat-value">{introStats.players}+</strong>
              </div>
              <div className="vpl-intro-stat-card">
                <span className="vpl-intro-stat-label">TEAMS</span>
                <strong className="vpl-intro-stat-value">{introStats.teams}+</strong>
              </div>
              {showPrizePool && (
              <div className="vpl-intro-stat-card">
                <span className="vpl-intro-stat-label">PRIZE POOL</span>
                <strong className="vpl-intro-stat-value">₹{new Intl.NumberFormat('en-IN').format(introStats.prizePool)}+</strong>
              </div>
              )}
            </div>
          </div>

          <div className="vpl-intro-showcase" aria-label="VPL showcase carousel">
            <div className="vpl-intro-showcase-frame">
              {introShowcaseSlides.map((slide, index) => (
                <article
                  key={slide.id}
                  className={`vpl-intro-slide ${index === activeIntroSlide ? 'vpl-intro-slide--active' : ''}`}
                  aria-hidden={index !== activeIntroSlide}
                >
                  <span className="vpl-intro-slide-label">{slide.label}</span>
                  <img src={slide.image} alt={slide.alt} className="vpl-intro-slide-image" loading="lazy" />
                </article>
              ))}
            </div>
            <div className="vpl-intro-showcase-dots" aria-label="Showcase slide controls">
              {introShowcaseSlides.map((slide, index) => (
                <button
                  key={`${slide.id}-dot`}
                  type="button"
                  className={`vpl-intro-showcase-dot ${index === activeIntroSlide ? 'vpl-intro-showcase-dot--active' : ''}`}
                  aria-label={`View ${slide.label} slide`}
                  onClick={() => setActiveIntroSlide(index)}
                >
                  <span
                    className="vpl-intro-showcase-dot-fill"
                    style={index === activeIntroSlide ? { animationDuration: '5000ms' } : {}}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="home-container-champions home-reveal" ref={section2Ref}>
        <div className="champions-wrapper">
          <h1 className="champions-heading">CHAMPIONS GALLERY</h1>
          <p className="champions-subtitle">Every season leaves a legacy. Here are the champions who owned the stage.</p>
          <div className="season-spotlight winners-strip">
            {championsData.map((champion) => {
              const isLatestChampion = champion.id === 'season-4';

              return (
                <div key={champion.id} className={`winner-item ${isLatestChampion ? 'winner-item--latest' : ''}`}>
                <div className="winner-image">
                  <img src={champion.image} alt={champion.alt} loading="lazy" />
                </div>
                <div className="winner-meta">
                  <div className="winner-season-row">
                    <span className="winner-season">{`${champion.season} • ${champion.year}`}</span>
                  </div>
                  {isLatestChampion && <span className="winner-latest-badge">LATEST CHAMPION</span>}
                  <span className="winner-team">{champion.team}</span>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
{/* ── TIMELINE SECTION ──────────────────────────────────────────────
   Drop-in replacement for the existing home-container-3 div in Home.jsx
   Everything else in Home.jsx stays the same.
─────────────────────────────────────────────────────────────────── */}

<div className="home-container-3 home-reveal" ref={section3Ref}>
  <div className="home-timeline-container">
    <h1 className="home-timeline-heading">TIMELINE</h1>

    <div className="home-timeline-showcase">

      {/* LEFT — clickable season entries */}
      <div className="home-timeline-line">
        {timelineEntries.map((entry) => {
          const isActive = entry.id === selectedTimelineEntry?.id;
          return (
            <button
              key={entry.id}
              className={`home-timeline-event${isActive ? ' home-timeline-event--active' : ''}`}
              onClick={() => setSelectedTimelineSeasonId(entry.id)}
              type="button"
            >
              <h3 className="home-timeline-season">{entry.seasonName}</h3>
              <p className="home-timeline-date">{entry.date}</p>
            </button>
          );
        })}
      </div>

      {/* RIGHT — detail panel */}
      {selectedTimelineEntry && (
        <div className={`home-timeline-details${isTimelineDetailsFading ? ' home-timeline-details--fading' : ''}`}>

          {/* Champion poster — fills the top of the panel */}
          <div className="home-timeline-details__poster-wrap">
            {selectedTimelineEntry.championLogo ? (
              <img
                src={selectedTimelineEntry.championLogo}
                alt={`${selectedTimelineEntry.seasonName} champion`}
                className="home-timeline-details__poster"
                loading="lazy"
              />
            ) : (
              <div className="home-timeline-details__poster-placeholder">
                <span className="home-timeline-details__poster-tbd">Season in Progress</span>
              </div>
            )}
            {/* Team name overlay on the poster */}
            <div className="home-timeline-details__poster-overlay">
              <span className="home-timeline-details__poster-team">
                {selectedTimelineEntry.winner !== 'TBD' ? selectedTimelineEntry.winner : 'Winner TBD'}
              </span>
            </div>
          </div>

          {/* Season label */}
          <h3 className="home-timeline-details__season">{selectedTimelineEntry.seasonName}</h3>

          {/* Stat grid */}
          <div className="home-timeline-details__grid">
            <p>
              <span>Winner</span>
              <strong>{selectedTimelineEntry.winner}</strong>
            </p>
            <p>
              <span>Teams</span>
              <strong>{selectedTimelineEntry.teams}</strong>
            </p>
            <p>
              <span>Prize Pool</span>
              <strong>{selectedTimelineEntry.prizePool}</strong>
            </p>
            <p>
              <span>Date</span>
              <strong>{selectedTimelineEntry.date}</strong>
            </p>
          </div>
        </div>
      )}
    </div>

    {/* Growth stats row */}
    <div className="vpl-growth-stats" aria-label="VPL growth statistics">
      <div className="vpl-growth-stats__card">
        <span className="vpl-growth-stats__value">5</span>
        <span className="vpl-growth-stats__label">Seasons</span>
      </div>
      <div className="vpl-growth-stats__card">
        <span className="vpl-growth-stats__value">64+</span>
        <span className="vpl-growth-stats__label">Teams</span>
      </div>
      <div className="vpl-growth-stats__card">
        <span className="vpl-growth-stats__value">300+</span>
        <span className="vpl-growth-stats__label">Players</span>
      </div>
      <div className="vpl-growth-stats__card">
        <span className="vpl-growth-stats__value">₹2,00,000+</span>
        <span className="vpl-growth-stats__label">Prize Pool Distributed</span>
      </div>
    </div>

  </div>
</div>
      <div className="home-footer home-reveal" ref={footerSectionRef}>
        <Footer />
      </div>
    </div>
  );
};

export default Home;